/**
 * Reads a value from a cookie and parses it from JSON.
 *
 * @template T - The type of value being stored
 * @param {string} key - The cookie key to read from
 * @param {T} defaultValue - The default value to return if the key doesn't exist or parsing fails
 * @returns {T} The parsed value from the cookie or the default value
 *
 * @example
 * const value = readCookieValue('theme', 'light');
 */
export function readCookieValue<T>(key: string, defaultValue: T): T {
  try {
    if (typeof document === 'undefined') {
      return defaultValue
    }

    const regex = new RegExp(`${key}=([^;]+)`, 'g')
    const cookie = document.cookie.match(regex) || [null]
    const [, value] = cookie[0]?.split('=') || [null, null]

    if (!value) {
      return defaultValue
    }

    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
    return JSON.parse(decodeURIComponent(value)) as T
  } catch {
    return defaultValue
  }
}

/**
 * Writes a value to a cookie after stringifying it to JSON.
 * Sets the cookie with the .buffer.com domain for cross-subdomain access.
 *
 * @template T - The type of value being stored
 * @param {string} key - The cookie key to write to
 * @param {T} value - The value to store
 * @param {number} [maxAge] - Cookie max age in seconds (defaults to 1 year)
 *
 * @example
 * writeCookieValue('theme', 'dark');
 */
export function writeCookieValue<T>(
  key: string,
  value: T,
  maxAge = 365 * 24 * 60 * 60, // 1 year in seconds
): void {
  try {
    if (typeof document === 'undefined') {
      return
    }

    const stringifiedValue = JSON.stringify(value)
    const encodedValue = encodeURIComponent(stringifiedValue)

    // Set cookie with .buffer.com domain for cross-subdomain access
    document.cookie = `${key}=${encodedValue}; domain=.buffer.com; path=/; max-age=${maxAge}; SameSite=Lax`
  } catch {
    // Silently fail - cookie storage is not critical for functionality
    // In production, this could be reported to error monitoring
  }
}

/**
 * Removes a cookie by setting its expiration date in the past.
 *
 * @param {string} key - The cookie key to remove
 *
 * @example
 * removeCookie('theme');
 */
export function removeCookie(key: string): void {
  try {
    if (typeof document === 'undefined') {
      return
    }

    document.cookie = `${key}=; domain=.buffer.com; path=/; expires=Thu, 01 Jan 1970 00:00:01 GMT`
  } catch {
    // Silently fail - cookie deletion is not critical
  }
}
